import { useRef, useState, type CSSProperties, type DragEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { DesignRequestKind, DesignRequestOrientation, Screen } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../i18n';
import { api, sha256Hex, uploadToSignedUrl } from '../../lib/api';
import { DESIGN_KINDS } from './designFormat';

export type NewDesignDraft = {
  kind: DesignRequestKind;
  description: string;
  orientation: DesignRequestOrientation;
  screenIds: string[];
  attachments: { filename: string; mediaAssetId: string | null }[];
};

type NewDesignRequestCardProps = {
  organisationId: string;
  screens: Screen[];
  busy: boolean;
  onSubmit: (draft: NewDesignDraft) => void;
};

type PendingFile = {
  key: string;
  filename: string;
  mediaAssetId: string | null;
};

function chipStyle(on: boolean): CSSProperties {
  return {
    fontFamily: 'inherit',
    fontSize: 13.5,
    fontWeight: 700,
    color: on ? 'var(--gs-accent-ink)' : 'var(--gs-ink2)',
    background: on ? 'var(--gs-accent-solid)' : 'var(--gs-panel2)',
    border: on ? 'none' : '1px solid var(--gs-line)',
    borderRadius: 999,
    padding: '7px 12px',
    cursor: 'pointer',
  };
}

function choiceStyle(on: boolean): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    fontFamily: 'inherit',
    color: on ? 'var(--gs-accent)' : 'var(--gs-ink2)',
    background: on ? 'var(--gs-panel2)' : 'transparent',
    border: `1.5px solid ${on ? 'var(--gs-accent)' : 'var(--gs-line)'}`,
    borderRadius: 12,
    padding: '16px 12px',
    cursor: 'pointer',
  };
}

export function NewDesignRequestCard({
  organisationId,
  screens,
  busy,
  onSubmit,
}: NewDesignRequestCardProps) {
  const { t } = useTranslation(['designer', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<DesignRequestKind>('menu_board');
  const [description, setDescription] = useState('');
  const [orientation, setOrientation] = useState<DesignRequestOrientation>('landscape');
  const [screenIds, setScreenIds] = useState<string[]>([]);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const canSend = description.trim().length > 0 && !busy && !uploading;

  const addFiles = async (list: FileList | File[] | null) => {
    if (!list || uploading) return;
    const incoming = Array.from(list);
    if (!incoming.length) return;

    setUploading(true);
    setUploadError(null);
    try {
      const next: PendingFile[] = [];
      for (const file of incoming) {
        if (file.type.startsWith('image/')) {
          const created = await api.createMediaAsset(organisationId, {
            kind: 'image',
            originalFilename: file.name,
            contentType: file.type || 'application/octet-stream',
          });
          await uploadToSignedUrl(created.uploadUrl, file);
          const checksumSha256 = await sha256Hex(file);
          await api.completeMediaAsset(organisationId, created.assetId, {
            checksumSha256,
            byteSize: file.size,
          });
          next.push({
            key: `${file.name}-${created.assetId}`,
            filename: file.name,
            mediaAssetId: created.assetId,
          });
        } else {
          next.push({
            key: `${file.name}-${file.size}-${file.lastModified}`,
            filename: file.name,
            mediaAssetId: null,
          });
        }
      }
      setFiles((current) => current.concat(next));
    } catch (error) {
      setUploadError(apiErrorMessage(error, t('form.uploadError')));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    void addFiles(event.dataTransfer.files);
  };

  return (
    <section
      className="flex flex-col gap-[22px] rounded-[14px] border p-6"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div className="flex flex-col gap-[5px]">
        <span className="text-[19px] font-extrabold tracking-[-0.02em]">{t('form.title')}</span>
        <span className="text-sm leading-[1.45]" style={{ color: 'var(--gs-ink2)' }}>
          {t('form.subtitle')}
        </span>
      </div>

      <form
        className="flex flex-col gap-[22px]"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (!canSend) return;
          onSubmit({
            kind,
            description: description.trim(),
            orientation,
            screenIds,
            attachments: files.map((file) => ({
              filename: file.filename,
              mediaAssetId: file.mediaAssetId,
            })),
          });
          setDescription('');
          setFiles([]);
          setScreenIds([]);
          setKind('menu_board');
          setOrientation('landscape');
        }}
      >
        <div className="flex flex-col gap-2.5">
          <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
            {t('form.kindLabel')}
          </span>
          <div className="flex flex-wrap gap-2">
            {DESIGN_KINDS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setKind(value)}
                style={chipStyle(kind === value)}
              >
                {t(`kinds.${value}`)}
              </button>
            ))}
          </div>
        </div>

        <label className="flex flex-col gap-[7px]">
          <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
            {t('form.describeLabel')}
          </span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={5}
            placeholder={t('form.describePlaceholder')}
            className="resize-y rounded-[10px] border px-[13px] py-3 text-[15px] leading-[1.45] outline-none focus:border-[var(--gs-accent)]"
            style={{
              color: 'var(--gs-ink)',
              background: 'var(--gs-field)',
              borderColor: 'var(--gs-line)',
              fontFamily: 'inherit',
            }}
          />
        </label>

        <div className="flex flex-col gap-2.5">
          <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
            {t('form.filesLabel')}
          </span>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xlsx,.csv"
            className="hidden"
            onChange={(event) => void addFiles(event.target.files)}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
            className="flex flex-col items-center gap-1 rounded-xl border-[1.5px] border-dashed px-4 py-[22px] disabled:opacity-50"
            style={{
              background: 'var(--gs-panel)',
              borderColor: 'var(--gs-line)',
              color: 'var(--gs-ink)',
              fontFamily: 'inherit',
            }}
          >
            <span className="text-[14.5px] font-bold">
              {uploading ? t('form.uploading') : t('form.dropFiles')}
            </span>
            <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
              {t('form.filesHint')}
            </span>
          </button>
          {files.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {files.map((file) => (
                <span
                  key={file.key}
                  className="flex items-center gap-2 rounded-full border py-[5px] ps-3 pe-1.5 text-[13px] font-semibold"
                  style={{ color: 'var(--gs-ink2)', borderColor: 'var(--gs-line)' }}
                >
                  {file.filename}
                  <button
                    type="button"
                    title={t('common:actions.remove')}
                    onClick={() =>
                      setFiles((current) => current.filter((item) => item.key !== file.key))
                    }
                    className="grid h-[22px] w-[22px] place-items-center rounded-full border-none text-sm leading-none"
                    style={{ background: 'var(--gs-panel2)', color: 'var(--gs-muted)' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {uploadError ? (
            <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }}>
              {uploadError}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
            {t('form.shapeLabel')}
          </span>
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => setOrientation('landscape')}
              style={choiceStyle(orientation === 'landscape')}
            >
              <span
                className="rounded"
                style={{ width: 40, height: 24, border: '2.5px solid currentColor' }}
              />
              <span className="text-sm font-bold">{t('form.landscape')}</span>
            </button>
            <button
              type="button"
              onClick={() => setOrientation('portrait')}
              style={choiceStyle(orientation === 'portrait')}
            >
              <span
                className="rounded"
                style={{ width: 20, height: 32, border: '2.5px solid currentColor' }}
              />
              <span className="text-sm font-bold">{t('form.portrait')}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="text-[13px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
            {t('form.screensLabel')}{' '}
            <span className="font-medium" style={{ color: 'var(--gs-muted)' }}>
              {t('form.optional')}
            </span>
          </span>
          <div className="flex flex-wrap gap-2">
            {screens.length === 0 ? (
              <span className="text-sm" style={{ color: 'var(--gs-muted)' }}>
                {t('form.noScreens')}
              </span>
            ) : (
              screens.map((screen) => {
                const on = screenIds.includes(screen.id);
                return (
                  <button
                    key={screen.id}
                    type="button"
                    onClick={() =>
                      setScreenIds((current) =>
                        on
                          ? current.filter((id) => id !== screen.id)
                          : current.concat([screen.id]),
                      )
                    }
                    style={chipStyle(on)}
                  >
                    {screen.name}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="submit"
            disabled={!canSend}
            className="rounded-[10px] border-none px-[18px] py-3.5 text-[15px] font-bold disabled:cursor-not-allowed"
            style={{
              color: 'var(--gs-accent-ink)',
              background: 'var(--gs-accent-solid)',
              opacity: canSend ? 1 : 0.45,
              fontFamily: 'inherit',
            }}
          >
            {busy ? t('form.sending') : t('form.send')}
          </button>
          <span className="text-center text-[13px]" style={{ color: 'var(--gs-muted)' }}>
            {t('form.approvalNote')}
          </span>
        </div>
      </form>
    </section>
  );
}
